import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import moment from 'moment';
import { formatCurrency } from '../Help/helper';
import BreadCrumb from '../BreadCrumb';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import logger from '../../utils/logger';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentCheckIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.default;

const PurchaseOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const pages = [
    { name: 'Procurement', to: '/procurement', component: Link, current: false },
    { name: 'Purchase Orders', to: '/procurement/purchase-orders', component: Link, current: false },
    { name: 'PO Details', to: '#', component: 'span', current: true },
  ];

  useEffect(() => {
    fetchPurchaseOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPurchaseOrder = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'purchaseOrders', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setPurchaseOrder({ id: docSnap.id, ...docSnap.data() });
      } else {
        toast.error('Purchase order not found');
        navigate('/procurement/purchase-orders');
      }
    } catch (error) {
      logger.error('Error fetching purchase order:', error);
      toast.error('Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Change status to ${newStatus}?`)) return;

    try {
      setUpdating(true);
      const docRef = doc(db, 'purchaseOrders', id);
      await updateDoc(docRef, {
        status: newStatus,
        statusHistory: [
          ...(purchaseOrder.statusHistory || []),
          {
            status: newStatus,
            date: Timestamp.now(),
            updatedBy: 'Current User',
          },
        ],
        lastUpdated: Timestamp.now(),
      });

      toast.success(`Status updated to ${newStatus}`);
      fetchPurchaseOrder();
    } catch (error) {
      logger.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const generatePDF = () => {
    if (!purchaseOrder) return;

    const documentDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 60],
      content: [
        {
          columns: [
            {
              width: '*',
              stack: [
                {
                  text: 'AYDEN AUTOMOBILE',
                  style: 'header',
                  fontSize: 20,
                  bold: true,
                  color: '#1a56db',
                },
                { text: 'Purchase Order', fontSize: 14, margin: [0, 5, 0, 0] },
              ],
            },
            {
              width: 'auto',
              stack: [
                {
                  text: `PO #: ${purchaseOrder.poNumber}`,
                  bold: true,
                  fontSize: 12,
                  alignment: 'right',
                },
                {
                  text: `Date: ${
                    purchaseOrder.createdAt?.toDate
                      ? moment(purchaseOrder.createdAt.toDate()).format('MMM DD, YYYY')
                      : 'N/A'
                  }`,
                  fontSize: 10,
                  alignment: 'right',
                  margin: [0, 5, 0, 0],
                },
                {
                  text: `Status: ${purchaseOrder.status}`,
                  fontSize: 10,
                  alignment: 'right',
                  margin: [0, 2, 0, 0],
                  bold: true,
                  color: purchaseOrder.status === 'Completed' ? 'green' : 'orange',
                },
              ],
            },
          ],
        },
        { text: '', margin: [0, 20] },
        {
          columns: [
            {
              width: '48%',
              stack: [
                { text: 'SUPPLIER', fontSize: 10, bold: true, color: '#666' },
                { text: purchaseOrder.supplier || 'N/A', fontSize: 11, bold: true, margin: [0, 5, 0, 0] },
                {
                  text: purchaseOrder.supplierAddress || '',
                  fontSize: 9,
                  margin: [0, 3, 0, 0],
                },
                {
                  text: purchaseOrder.supplierPhone ? `Tel: ${purchaseOrder.supplierPhone}` : '',
                  fontSize: 9,
                  margin: [0, 2, 0, 0],
                },
                {
                  text: purchaseOrder.supplierEmail ? `Email: ${purchaseOrder.supplierEmail}` : '',
                  fontSize: 9,
                  margin: [0, 2, 0, 0],
                },
              ],
            },
            {
              width: '4%',
              text: '',
            },
            {
              width: '48%',
              stack: [
                { text: 'DELIVER TO', fontSize: 10, bold: true, color: '#666' },
                { text: 'Ayden Automobile Workshop', fontSize: 11, bold: true, margin: [0, 5, 0, 0] },
                {
                  text: 'Workshop Address',
                  fontSize: 9,
                  margin: [0, 3, 0, 0],
                },
                {
                  text: purchaseOrder.deliveryDate
                    ? `Required By: ${moment(purchaseOrder.deliveryDate).format('MMM DD, YYYY')}`
                    : '',
                  fontSize: 9,
                  margin: [0, 2, 0, 0],
                  bold: true,
                },
              ],
            },
          ],
        },
        { text: '', margin: [0, 20] },
        purchaseOrder.procurementNumber
          ? {
              text: `Reference: Procurement #${purchaseOrder.procurementNumber}`,
              fontSize: 9,
              italics: true,
              color: '#666',
              margin: [0, 0, 0, 10],
            }
          : {},
        {
          table: {
            headerRows: 1,
            widths: ['5%', '45%', '15%', '15%', '20%'],
            body: [
              [
                { text: '#', style: 'tableHeader' },
                { text: 'Description', style: 'tableHeader' },
                { text: 'Quantity', style: 'tableHeader', alignment: 'center' },
                { text: 'Unit Price', style: 'tableHeader', alignment: 'right' },
                { text: 'Total', style: 'tableHeader', alignment: 'right' },
              ],
              ...(purchaseOrder.items || []).map((item, index) => [
                { text: index + 1, fontSize: 9 },
                { text: item.description || '', fontSize: 9 },
                { text: item.quantity || 0, fontSize: 9, alignment: 'center' },
                { text: formatCurrency(item.price || 0), fontSize: 9, alignment: 'right' },
                {
                  text: formatCurrency((item.price || 0) * (item.quantity || 0)),
                  fontSize: 9,
                  alignment: 'right',
                },
              ]),
            ],
          },
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5;
            },
            vLineWidth: function () {
              return 0.5;
            },
            hLineColor: function (i) {
              return i === 1 ? '#1a56db' : '#ddd';
            },
            vLineColor: function () {
              return '#ddd';
            },
          },
        },
        {
          columns: [
            { width: '*', text: '' },
            {
              width: 200,
              stack: [
                {
                  columns: [
                    { text: 'Subtotal:', fontSize: 10, alignment: 'right', width: '50%' },
                    {
                      text: formatCurrency(purchaseOrder.subtotal || 0),
                      fontSize: 10,
                      alignment: 'right',
                      width: '50%',
                    },
                  ],
                  margin: [0, 10, 0, 5],
                },
                purchaseOrder.taxes
                  ? {
                      columns: [
                        { text: 'Tax (7.5%):', fontSize: 10, alignment: 'right', width: '50%' },
                        {
                          text: formatCurrency(purchaseOrder.taxes || 0),
                          fontSize: 10,
                          alignment: 'right',
                          width: '50%',
                        },
                      ],
                      margin: [0, 0, 0, 5],
                    }
                  : {},
                {
                  columns: [
                    { text: 'Grand Total:', fontSize: 12, bold: true, alignment: 'right', width: '50%' },
                    {
                      text: formatCurrency(purchaseOrder.grandTotal || 0),
                      fontSize: 12,
                      bold: true,
                      alignment: 'right',
                      width: '50%',
                    },
                  ],
                  margin: [0, 5, 0, 0],
                },
              ],
            },
          ],
        },
        { text: '', margin: [0, 20] },
        { text: 'Terms & Conditions:', fontSize: 10, bold: true },
        {
          ul: [
            'Please acknowledge receipt of this purchase order',
            'Delivery must be made by the specified date',
            'All items must meet quality specifications',
            'Invoice must reference this PO number',
            purchaseOrder.paymentTerms || 'Payment terms as agreed',
          ],
          fontSize: 9,
          margin: [0, 5, 0, 0],
        },
        purchaseOrder.notes
          ? [
              { text: '', margin: [0, 10] },
              { text: 'Additional Notes:', fontSize: 10, bold: true },
              { text: purchaseOrder.notes, fontSize: 9, margin: [0, 5, 0, 0] },
            ]
          : {},
        { text: '', margin: [0, 30] },
        {
          columns: [
            {
              width: '50%',
              stack: [
                { text: '________________________', alignment: 'center' },
                { text: 'Authorized Signature', fontSize: 9, alignment: 'center', margin: [0, 5, 0, 0] },
                { text: 'Ayden Automobile', fontSize: 8, alignment: 'center', margin: [0, 2, 0, 0] },
              ],
            },
            {
              width: '50%',
              stack: [
                { text: '________________________', alignment: 'center' },
                { text: 'Supplier Signature', fontSize: 9, alignment: 'center', margin: [0, 5, 0, 0] },
                { text: 'Date', fontSize: 8, alignment: 'center', margin: [0, 2, 0, 0] },
              ],
            },
          ],
        },
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
        },
        tableHeader: {
          bold: true,
          fontSize: 10,
          fillColor: '#f3f4f6',
        },
      },
    };

    pdfMake.createPdf(documentDefinition).download(`PO_${purchaseOrder.poNumber}.pdf`);
    toast.success('PDF downloaded successfully');
  };

  const getStatusBadge = (status) => {
    const badges = {
      Draft: 'bg-gray-100 text-gray-800',
      Sent: 'bg-blue-100 text-blue-800',
      Acknowledged: 'bg-amber-100 text-amber-800',
      'Partially Received': 'bg-orange-100 text-orange-800',
      Completed: 'bg-emerald-100 text-emerald-800',
      Cancelled: 'bg-red-100 text-red-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-cyan-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  if (!purchaseOrder) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-cyan-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="text-center py-12">
          <DocumentTextIcon className="h-16 w-16 text-white mx-auto mb-4" />
          <p className="text-white font-medium">Purchase order not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-cyan-600 py-4">
      <BreadCrumb pages={pages} />
      <div className="container mx-auto px-4">
        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900">{purchaseOrder.poNumber}</h1>
                <p className="text-xs text-gray-600 mt-0.5">
                  Created on {purchaseOrder.createdAt?.toDate ? moment(purchaseOrder.createdAt.toDate()).format('MMMM DD, YYYY') : 'N/A'}
                </p>
              </div>
            </div>
            <span className={`px-4 py-2 inline-flex text-sm font-black rounded-full ${getStatusBadge(purchaseOrder.status)}`}>
              {purchaseOrder.status}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={generatePDF}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition-all duration-200"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
              Download PDF
            </button>

            {(purchaseOrder.status === 'Sent' || purchaseOrder.status === 'Acknowledged') && (
              <Link
                to={`/procurement/create-grn/${id}`}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
              >
                <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1.5" />
                Create GRN
              </Link>
            )}

            {purchaseOrder.status === 'Draft' && (
              <button
                onClick={() => handleStatusChange('Sent')}
                disabled={updating}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 w-4 mr-1.5" />
                Send to Supplier
              </button>
            )}

            {purchaseOrder.status === 'Sent' && (
              <button
                onClick={() => handleStatusChange('Acknowledged')}
                disabled={updating}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
              >
                <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                Mark Acknowledged
              </button>
            )}

            {(purchaseOrder.status === 'Acknowledged' || purchaseOrder.status === 'Partially Received') && (
              <button
                onClick={() => handleStatusChange('Completed')}
                disabled={updating}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
              >
                <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                Mark Completed
              </button>
            )}

            {purchaseOrder.status !== 'Cancelled' && purchaseOrder.status !== 'Completed' && (
              <button
                onClick={() => handleStatusChange('Cancelled')}
                disabled={updating}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-red-500 to-pink-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
              >
                <XCircleIcon className="h-4 w-4 mr-1.5" />
                Cancel PO
              </button>
            )}
          </div>
        </div>

        {/* Details Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Supplier Information */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-cyan-600 -m-4 p-4 rounded-t-xl">
              <BuildingOfficeIcon className="h-5 w-5 text-white" />
              <h2 className="text-sm font-black text-white">Supplier Information</h2>
            </div>
            <dl className="space-y-2 mt-4">
              <div>
                <dt className="text-xs font-medium text-gray-500">Company Name</dt>
                <dd className="text-xs font-bold text-gray-900 mt-0.5">{purchaseOrder.supplier || 'N/A'}</dd>
              </div>
              {purchaseOrder.supplierAddress && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Address</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">{purchaseOrder.supplierAddress}</dd>
                </div>
              )}
              {purchaseOrder.supplierPhone && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Phone</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">{purchaseOrder.supplierPhone}</dd>
                </div>
              )}
              {purchaseOrder.supplierEmail && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Email</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">{purchaseOrder.supplierEmail}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Order Information */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-cyan-600 -m-4 p-4 rounded-t-xl">
              <CalendarIcon className="h-5 w-5 text-white" />
              <h2 className="text-sm font-black text-white">Order Information</h2>
            </div>
            <dl className="space-y-2 mt-4">
              {purchaseOrder.procurementNumber && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Procurement #</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">
                    <Link
                      to={`/procurement/requisitions/${purchaseOrder.procurementId}`}
                      className="text-blue-600 hover:underline"
                    >
                      {purchaseOrder.procurementNumber}
                    </Link>
                  </dd>
                </div>
              )}
              {purchaseOrder.deliveryDate && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Required By</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">
                    {moment(purchaseOrder.deliveryDate).format('MMMM DD, YYYY')}
                  </dd>
                </div>
              )}
              {purchaseOrder.paymentTerms && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Payment Terms</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">{purchaseOrder.paymentTerms}</dd>
                </div>
              )}
              {purchaseOrder.createdBy && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Created By</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">{purchaseOrder.createdBy}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Financial Summary */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-cyan-600 -m-4 p-4 rounded-t-xl">
              <BanknotesIcon className="h-5 w-5 text-white" />
              <h2 className="text-sm font-black text-white">Financial Summary</h2>
            </div>
            <dl className="space-y-2 mt-4">
              <div className="flex justify-between">
                <dt className="text-xs font-medium text-gray-500">Subtotal</dt>
                <dd className="text-xs font-bold text-gray-900">{formatCurrency(purchaseOrder.subtotal || 0)}</dd>
              </div>
              {purchaseOrder.taxes > 0 && (
                <div className="flex justify-between">
                  <dt className="text-xs font-medium text-gray-500">Tax (7.5%)</dt>
                  <dd className="text-xs font-bold text-gray-900">{formatCurrency(purchaseOrder.taxes || 0)}</dd>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <dt className="text-sm font-black text-gray-900">Grand Total</dt>
                <dd className="text-sm font-black text-blue-600">
                  {formatCurrency(purchaseOrder.grandTotal || 0)}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-4">
          <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-600">
            <h2 className="text-sm font-black text-white">Items</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-gray-700 uppercase">#</th>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-gray-700 uppercase">Description</th>
                  <th className="px-2 py-1.5 text-center text-xs font-black text-gray-700 uppercase">Quantity</th>
                  <th className="px-2 py-1.5 text-right text-xs font-black text-gray-700 uppercase">Unit Price</th>
                  <th className="px-2 py-1.5 text-right text-xs font-black text-gray-700 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(purchaseOrder.items || []).map((item, index) => (
                  <tr key={index} className="hover:bg-blue-50 transition-colors duration-150">
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium text-gray-900">{index + 1}</td>
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-900">{item.description}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-bold text-gray-900 text-center">
                      {item.quantity}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium text-gray-900 text-right">
                      {formatCurrency(item.price)}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-black text-gray-900 text-right">
                      {formatCurrency(item.price * item.quantity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Additional Notes */}
        {purchaseOrder.notes && (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
            <h2 className="text-sm font-black text-gray-900 mb-3">Additional Notes</h2>
            <p className="text-xs font-medium text-gray-700 whitespace-pre-wrap">{purchaseOrder.notes}</p>
          </div>
        )}

        {/* Status History */}
        {purchaseOrder.statusHistory && purchaseOrder.statusHistory.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-4">
            <h2 className="text-sm font-black text-gray-900 mb-4">Status History</h2>
            <div className="flow-root">
              <ul className="-mb-8">
                {purchaseOrder.statusHistory.map((history, index) => (
                  <li key={index}>
                    <div className="relative pb-8">
                      {index !== purchaseOrder.statusHistory.length - 1 && (
                        <span
                          className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        ></span>
                      )}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-600 flex items-center justify-center ring-8 ring-white">
                            <CheckCircleIcon className="h-5 w-5 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                          <div>
                            <p className="text-xs font-medium text-gray-900">
                              Status changed to <span className="font-black">{history.status}</span>
                            </p>
                          </div>
                          <div className="text-right text-xs whitespace-nowrap text-gray-500 font-medium">
                            {history.date?.toDate
                              ? moment(history.date.toDate()).format('MMM DD, YYYY HH:mm')
                              : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrderDetail;
